import { useEffect, useState, useCallback } from 'react';
import {
  StaffPermissions,
  buildFullPermissions,
  buildEmptyPermissions,
  firstVisiblePage,
  SUPER_ROLES,
} from '../constants/permissions';
import { socketService } from '../services/socketService';
import * as lordsApi from '../services/lordsApi';

const CACHE_KEY = 'myPermissions';

function loadCache(): StaffPermissions | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as StaffPermissions) : null;
  } catch {
    return null;
  }
}

function saveCache(p: StaffPermissions) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(p));
  } catch {}
}

interface UsePermissionsResult {
  permissions: StaffPermissions;
  role: string;
  isSuper: boolean;
  loading: boolean;
  can: (key: string) => boolean;
  pageVisible: (path: string) => boolean;
  groupVisible: (title: string) => boolean;
  /** Where to send this user when they hit "/" — never a page they can't open. */
  landingPath: string;
  refresh: () => Promise<void>;
}

export function usePermissions(): UsePermissionsResult {
  const role = localStorage.getItem('userRole') || 'Guest';
  const isSuper = (SUPER_ROLES as readonly string[]).includes(role);

  const [permissions, setPermissions] = useState<StaffPermissions>(() => {
    if (isSuper) return buildFullPermissions();
    return loadCache() ?? buildEmptyPermissions();
  });
  const [loading, setLoading] = useState(!isSuper && loadCache() === null);

  const refresh = useCallback(async () => {
    if (isSuper) return;
    try {
      const data = await lordsApi.getMyPermissions();
      if (data?.permissions) {
        setPermissions(data.permissions);
        saveCache(data.permissions);
      }
    } catch (e) {
      console.error('[Permissions] refresh failed', e);
    } finally {
      setLoading(false);
    }
  }, [isSuper]);

  useEffect(() => {
    if (isSuper) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    socketService.connect(token);
    socketService.subscribeToMyPermissions((data) => {
      if (data?.permissions) {
        setPermissions(data.permissions);
        saveCache(data.permissions);
        setLoading(false);
      }
    });

    refresh();

    return () => {
      socketService.unsubscribeFromMyPermissions();
    };
  }, [isSuper, refresh]);

  const can = useCallback(
    (key: string) => {
      if (isSuper) return true;
      return Boolean(permissions.authority?.[key]);
    },
    [isSuper, permissions]
  );

  const groupVisible = useCallback(
    (title: string) => {
      if (isSuper) return true;
      return Boolean(permissions.groups?.[title]);
    },
    [isSuper, permissions]
  );

  const pageVisible = useCallback(
    (path: string) => {
      if (isSuper) return true;
      return Boolean(permissions.pages?.[path]);
    },
    [isSuper, permissions]
  );

  /* Super roles keep the historic home. Everyone else goes to the first page
     their grants actually allow, falling back to /admin-management only when
     they have nothing at all (that page then renders its own Access Denied). */
  const landingPath = isSuper
    ? '/admin-management'
    : firstVisiblePage(permissions) ?? '/admin-management';

  return { permissions, role, isSuper, loading, can, pageVisible, groupVisible, landingPath, refresh };
}
